import type {
  CalendarEvent,
  CalendarFeed,
  CreateFeed,
  EventInput,
  UpdateFeed,
} from "@ronin/contracts";
import { toast } from "@ronin/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api.js";
import { showError } from "../../lib/errors.js";

/** spec03 FR12: switching periods or tabs back and forth does not hit the API again. */
const STALE_MS = 5 * 60_000;

export const calendarKey = ["calendar"] as const;

export const itemsQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: [...calendarKey, "items", from, to],
    queryFn: () => api.calendar.items({ from, to }),
    staleTime: STALE_MS,
    placeholderData: (previous) => previous,
  });

export const feedsQuery = queryOptions({
  queryKey: [...calendarKey, "feeds"],
  queryFn: () => api.calendar.feeds(),
  staleTime: STALE_MS,
});

/** Items for the visible range plus the feeds (for colours and visibility). */
export function useCalendarData(from: string, to: string) {
  const items = useQuery(itemsQuery(from, to));
  const feeds = useQuery(feedsQuery);
  return { items, feeds };
}

export function useEventMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: [...calendarKey, "items"] });

  const create = useMutation({
    mutationFn: (input: EventInput) => api.calendar.createEvent(input),
    onSuccess: refresh,
    onError: showError("the event"),
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: EventInput }) =>
      api.calendar.updateEvent(id, input),
    onSuccess: refresh,
    onError: showError("the event"),
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.calendar.restoreEvent(id),
    onSuccess: refresh,
    onError: showError("the event", "restored"),
  });
  const remove = useMutation({
    mutationFn: (event: CalendarEvent) => api.calendar.removeEvent(event.id),
    onSuccess: (_result, event) => {
      void refresh();
      toast("Event deleted", {
        description: event.title,
        duration: 8000,
        action: { label: "Undo", onClick: () => restore.mutate(event.id) },
      });
    },
    onError: showError("the event", "deleted"),
  });
  return { create, update, remove };
}

export function useFeedMutations() {
  const queryClient = useQueryClient();
  const refreshAll = () => queryClient.invalidateQueries({ queryKey: calendarKey });

  /** Errors of add/edit are shown inside the dialog, so these two have no toast. */
  const create = useMutation({
    mutationFn: (input: CreateFeed) => api.calendar.createFeed(input),
    onSuccess: refreshAll,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateFeed }) =>
      api.calendar.updateFeed(id, patch),
    onSuccess: refreshAll,
  });

  /** The eye: hides or shows at once (FR10a), then syncs with the server. */
  const setVisible = useMutation({
    mutationFn: ({ feed, visible }: { feed: CalendarFeed; visible: boolean }) =>
      api.calendar.updateFeed(feed.id, { visible }),
    onMutate: async ({ feed, visible }) => {
      await queryClient.cancelQueries({ queryKey: feedsQuery.queryKey });
      const previous = queryClient.getQueryData<CalendarFeed[]>(feedsQuery.queryKey);
      queryClient.setQueryData<CalendarFeed[]>(feedsQuery.queryKey, (feeds = []) =>
        feeds.map((f) => (f.id === feed.id ? { ...f, visible } : f)),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(feedsQuery.queryKey, context.previous);
      showError("the feed")(error);
    },
    onSettled: refreshAll,
  });

  const refresh = useMutation({
    mutationFn: (feed: CalendarFeed) => api.calendar.refreshFeed(feed.id),
    onSuccess: (feed) => {
      void refreshAll();
      if (feed.lastError) toast.error(`${feed.name}: the refresh failed.`);
    },
    onError: showError("the feed", "refreshed"),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.calendar.restoreFeed(id),
    onSuccess: refreshAll,
    onError: showError("the feed", "restored"),
  });
  const remove = useMutation({
    mutationFn: (feed: CalendarFeed) => api.calendar.removeFeed(feed.id),
    onSuccess: (_result, feed) => {
      void refreshAll();
      toast("Feed removed", {
        description: feed.name,
        duration: 8000,
        action: { label: "Undo", onClick: () => restore.mutate(feed.id) },
      });
    },
    onError: showError("the feed", "removed"),
  });

  return { create, update, setVisible, refresh, remove };
}
